import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../interceptors";

const FALLBACK_PALETTE = [
  "#8B5CF6",
  "#3B82F6",
  "#10B981",
  "#F59E0B",
  "#EF4444",
  "#06B6D4",
  "#EC4899",
  "#84CC16",
];
const colorFromKey = (key = "") => {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) | 0;
  return FALLBACK_PALETTE[Math.abs(h) % FALLBACK_PALETTE.length];
};

const directionToDir = (d) => (d === "OUTBOUND" ? "out" : "in");

const mapMessage = (m) => ({
  id: m.id,
  dir: directionToDir(m.direction),
  text: m.text ?? "",
  at: m.received_at,
  status: m.status || "DELIVERED",
  template: m.template_name || null,
  // Populated when GupShup / Meta reports a delivery failure on the row.
  error: m.error || null,
});

const mapConversation = (c) => {
  const lastAt =
    c.last_inbound_at && c.last_outbound_at
      ? new Date(c.last_inbound_at) > new Date(c.last_outbound_at)
        ? c.last_inbound_at
        : c.last_outbound_at
      : c.last_inbound_at || c.last_outbound_at || null;
  const preview = c.last_message
    ? [
        {
          id: `preview-${c.id}`,
          dir: directionToDir(c.last_message.direction),
          text: c.last_message.text ?? "",
          at: c.last_message.at,
          status: c.last_message.status || "DELIVERED",
          template: null,
        },
      ]
    : [];
  return {
    id: c.id,
    candidateId: c.candidate_id ?? null,
    waId: c.candidate_wa_id,
    businessPhoneNumberId: c.business_phone_number_id,
    name: c.candidate_name || c.candidate_phone || c.candidate_wa_id,
    phone:
      c.candidate_phone || (c.candidate_wa_id ? `+${c.candidate_wa_id}` : ""),
    avatar:
      c.avatar_initials ||
      (c.candidate_name || c.candidate_wa_id || "?").slice(0, 2).toUpperCase(),
    color: c.avatar_color || colorFromKey(c.candidate_wa_id || String(c.id)),
    unread: c.unread_count || 0,
    lastSeen: lastAt,
    messagesLoaded: false,
    messages: preview,
  };
};

const relTime = (iso) => {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.round(h / 24)}d`;
};
const clockTime = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const WhatsAppInbox = () => {
  const navigate = useNavigate();
  const [convos, setConvos] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [threadLoading, setThreadLoading] = useState(false);
  const [error, setError] = useState(null);
  const threadRef = useRef(null);

  const tenantId = useMemo(
    () => Number(localStorage.getItem("tenantId")) || 1,
    [],
  );

  useEffect(() => {
    document.title = "WhatsApp inbox - Ample Hire";
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await axiosInstance.get(
          `/job-posting/api/whatsapp/${tenantId}/conversations`,
        );
        if (cancelled) return;
        const list = (res?.data?.conversations || []).map(mapConversation);
        setConvos(list);
        if (list.length > 0) setSelectedId(list[0].id);
      } catch (e) {
        if (!cancelled) setError(e?.message || "Failed to load conversations");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  // Lazily load the full thread the first time a conversation is opened.
  useEffect(() => {
    if (!selectedId) return;
    const target = convos.find((c) => c.id === selectedId);
    if (!target || target.messagesLoaded) return;

    let cancelled = false;
    (async () => {
      try {
        setThreadLoading(true);
        const res = await axiosInstance.get(
          `/job-posting/api/whatsapp/${tenantId}/conversations/${selectedId}/messages`,
        );
        if (cancelled) return;
        const msgs = (res?.data?.messages || []).map(mapMessage);
        const meta = res?.data?.conversation || null;
        setConvos((cs) =>
          cs.map((c) =>
            c.id !== selectedId
              ? c
              : {
                  ...c,
                  name: meta?.candidate_name || c.name,
                  phone: meta?.candidate_phone || c.phone,
                  avatar: meta?.avatar_initials || c.avatar,
                  color: meta?.avatar_color || c.color,
                  lastSeen: meta?.last_seen_at || c.lastSeen,
                  messages: msgs,
                  messagesLoaded: true,
                },
          ),
        );
      } catch (e) {
        if (!cancelled) setError(e?.message || "Failed to load messages");
      } finally {
        if (!cancelled) setThreadLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, tenantId]);

  const selected = useMemo(
    () => convos.find((c) => c.id === selectedId),
    [convos, selectedId],
  );
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return convos;
    return convos.filter(
      (c) =>
        (c.name || "").toLowerCase().includes(q) || (c.phone || "").includes(q),
    );
  }, [convos, search]);

  useEffect(() => {
    if (threadRef.current)
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [selectedId, selected?.messages.length]);

  const onSelect = (id) => {
    setSelectedId(id);
    setConvos((cs) => cs.map((c) => (c.id === id ? { ...c, unread: 0 } : c)));
  };

  const onSend = async () => {
    const text = draft.trim();
    if (!text || !selected) return;
    // Capture conversation id locally — `selected` is a useMemo and can drift
    // if the user clicks another thread mid-flight.
    const convId = selected.id;
    const tempId = `tmp-${Date.now()}`;
    const optimistic = {
      id: tempId,
      dir: "out",
      text,
      at: new Date().toISOString(),
      status: "SENDING",
      template: null,
      error: null,
    };
    setConvos((cs) =>
      cs.map((c) =>
        c.id === convId ? { ...c, messages: [...c.messages, optimistic] } : c,
      ),
    );
    setDraft("");
    try {
      const res = await axiosInstance.post(
        `/job-posting/api/whatsapp/${tenantId}/conversations/${convId}/messages`,
        { text, replyToMessageId: null },
      );
      if (res?.data?.success === false) {
        throw new Error(res.data?.message || "Failed to send message.");
      }
      const serverMsg = mapMessage(res.data?.data || {});
      setConvos((cs) =>
        cs.map((c) =>
          c.id !== convId
            ? c
            : {
                ...c,
                messages: c.messages.map((m) =>
                  m.id === tempId ? serverMsg : m,
                ),
              },
        ),
      );
    } catch (err) {
      const errMsg =
        err.response?.data?.message || err.message || "Failed to send.";
      setConvos((cs) =>
        cs.map((c) =>
          c.id !== convId
            ? c
            : {
                ...c,
                messages: c.messages.map((m) =>
                  m.id === tempId
                    ? { ...m, status: "FAILED", error: errMsg }
                    : m,
                ),
              },
        ),
      );
      // Restore the draft so the user can retry without retyping.
      setDraft(text);
    }
  };

  return (
    <div
      className="app-content page-enter"
      style={{
        maxWidth: 1200,
        margin: "0 auto",
        padding: "16px 16px 32px",
        height: "calc(100vh - 120px)",
      }}
    >
      <style>{`
        .wa-bubble { max-width: 72%; padding: 8px 12px; border-radius: 12px; font-size: 13px; line-height: 1.4; position: relative; box-shadow: 0 1px 2px rgba(0,0,0,0.04); }
        .wa-bubble-out { background: #DCF8C6; color: #0F172A; border-bottom-right-radius: 4px; margin-left: auto; }
        .wa-bubble-in { background: white; color: #0F172A; border-bottom-left-radius: 4px; border: 1px solid #F1F5F9; }
        .wa-bubble-meta { font-size: 10px; color: #64748B; margin-top: 4px; display: flex; gap: 4px; align-items: center; justify-content: flex-end; }
        .wa-check { font-size: 11px; letter-spacing: -2px; }
        .wa-check-sent { color: #94A3B8; }
        .wa-check-delivered { color: #94A3B8; }
        .wa-check-read { color: #0EA5E9; }
        .wa-template-pill { display: inline-block; font-size: 9px; padding: 1px 6px; border-radius: 100px; background: rgba(99,102,241,0.1); color: #7B5AC4; margin-right: 6px; font-weight: 600; }
      `}</style>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          marginBottom: 12,
        }}
      >
        <div>
          <h1
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: "#0F172A",
              margin: 0,
            }}
          >
            WhatsApp inbox
          </h1>
          <p style={{ fontSize: 12, color: "#64748B", margin: "2px 0 0" }}>
            {loading
              ? "Loading conversations…"
              : `${convos.length} conversations · via Meta WhatsApp Business`}
            {error && (
              <span style={{ color: "#EF4444", marginLeft: 8 }}>· {error}</span>
            )}
          </p>
        </div>
        <button
          onClick={() => navigate("/admin/whatsapp-settings")}
          style={btnGhost}
        >
          Settings
        </button>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "320px 1fr",
          gap: 0,
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: 12,
          overflow: "hidden",
          height: "calc(100% - 60px)",
        }}
      >
        {/* Conversation list */}
        <div
          style={{
            borderRight: "1px solid #E2E8F0",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ padding: 10, borderBottom: "1px solid #F1F5F9" }}>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              style={{
                width: "100%",
                padding: "7px 10px",
                border: "1px solid #E2E8F0",
                borderRadius: 100,
                fontSize: 12,
                outline: "none",
                boxSizing: "border-box",
              }}
            />
          </div>
          <div style={{ flex: 1, overflowY: "auto" }}>
            {loading && (
              <div
                style={{
                  padding: 16,
                  fontSize: 12,
                  color: "#64748B",
                  textAlign: "center",
                }}
              >
                Loading…
              </div>
            )}
            {!loading && filtered.length === 0 && (
              <div
                style={{
                  padding: 16,
                  fontSize: 12,
                  color: "#94A3B8",
                  textAlign: "center",
                }}
              >
                {convos.length === 0 ? "No conversations yet" : "No matches"}
              </div>
            )}
            {filtered.map((c) => {
              const last = c.messages[c.messages.length - 1];
              const active = c.id === selectedId;
              return (
                <div
                  key={c.id}
                  onClick={() => onSelect(c.id)}
                  style={{
                    padding: "10px 12px",
                    display: "flex",
                    gap: 10,
                    alignItems: "center",
                    cursor: "pointer",
                    background: active ? "#F8FAFC" : "white",
                    borderLeft: active
                      ? "3px solid #25D366"
                      : "3px solid transparent",
                    borderBottom: "1px solid #F1F5F9",
                  }}
                >
                  <div
                    style={{
                      width: 38,
                      height: 38,
                      borderRadius: 19,
                      background: c.color,
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 700,
                      fontSize: 13,
                      flexShrink: 0,
                    }}
                  >
                    {c.avatar}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "baseline",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: 13,
                          color: "#0F172A",
                        }}
                      >
                        {c.name}
                      </div>
                      <div style={{ fontSize: 10, color: "#94A3B8" }}>
                        {last ? relTime(last.at) : ""}
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          color: "#64748B",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: 200,
                        }}
                      >
                        {last?.dir === "out" && (
                          <span
                            style={{
                              color:
                                last.status === "READ" ? "#0EA5E9" : "#94A3B8",
                              marginRight: 4,
                            }}
                          >
                            ✓✓
                          </span>
                        )}
                        {last ? (
                          last.text
                        ) : (
                          <span
                            style={{ fontStyle: "italic", color: "#94A3B8" }}
                          >
                            No messages yet
                          </span>
                        )}
                      </div>
                      {c.unread > 0 && (
                        <span
                          style={{
                            background: "#25D366",
                            color: "white",
                            fontSize: 9,
                            fontWeight: 700,
                            padding: "1px 6px",
                            borderRadius: 10,
                            minWidth: 16,
                            textAlign: "center",
                          }}
                        >
                          {c.unread}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Thread */}
        {selected && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              background: "#ECE5DD",
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.85), rgba(255,255,255,0.85))",
            }}
          >
            <div
              style={{
                padding: "10px 16px",
                background: "#F0F2F5",
                borderBottom: "1px solid #E2E8F0",
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 18,
                  background: selected.color,
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: 12,
                }}
              >
                {selected.avatar}
              </div>
              <div>
                <div
                  style={{ fontSize: 13, fontWeight: 600, color: "#0F172A" }}
                >
                  {selected.name}
                </div>
                <div style={{ fontSize: 11, color: "#64748B" }}>
                  {selected.phone}
                  {selected.lastSeen
                    ? ` · last seen ${relTime(selected.lastSeen)} ago`
                    : ""}
                </div>
              </div>
            </div>
            <div
              ref={threadRef}
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "16px 20px",
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              {threadLoading && !selected.messagesLoaded && (
                <div
                  style={{
                    alignSelf: "center",
                    fontSize: 11,
                    color: "#64748B",
                    padding: "8px 0",
                  }}
                >
                  Loading messages…
                </div>
              )}
              {!threadLoading &&
                selected.messagesLoaded &&
                selected.messages.length === 0 && (
                  <div
                    style={{
                      alignSelf: "center",
                      fontSize: 11,
                      color: "#94A3B8",
                      padding: "8px 0",
                    }}
                  >
                    No messages in this conversation yet.
                  </div>
                )}
              {selected.messages.map((m) => (
                <div
                  key={m.id}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: m.dir === "out" ? "flex-end" : "flex-start",
                  }}
                >
                  <div
                    className={`wa-bubble wa-bubble-${m.dir}`}
                    style={
                      m.status === "SENDING" ? { opacity: 0.65 } : undefined
                    }
                  >
                    {m.template && m.dir === "out" && (
                      <span className="wa-template-pill">{m.template}</span>
                    )}
                    <div>{m.text}</div>
                    <div className="wa-bubble-meta">
                      <span>{clockTime(m.at)}</span>
                      {m.dir === "out" && m.status && (
                        <span
                          className={`wa-check wa-check-${m.status.toLowerCase()}`}
                        >
                          {m.status === "SENT" && "✓"}
                          {(m.status === "DELIVERED" || m.status === "READ") &&
                            "✓✓"}
                          {m.status === "FAILED" && (
                            <span style={{ color: "#EF4444" }}>!</span>
                          )}
                          {/* SENDING: no tick — bubble opacity signals pending. */}
                        </span>
                      )}
                    </div>
                  </div>
                  {m.status === "FAILED" && m.error && (
                    <div
                      style={{
                        fontSize: 10,
                        color: "#EF4444",
                        marginTop: 2,
                        maxWidth: "72%",
                      }}
                    >
                      Failed: {m.error}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div
              style={{
                padding: 10,
                background: "#F0F2F5",
                borderTop: "1px solid #E2E8F0",
                display: "flex",
                gap: 8,
                alignItems: "center",
              }}
            >
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) =>
                  e.key === "Enter" &&
                  !e.shiftKey &&
                  (e.preventDefault(), onSend())
                }
                placeholder="Type a message"
                style={{
                  flex: 1,
                  padding: "10px 14px",
                  border: "1px solid #E2E8F0",
                  borderRadius: 100,
                  fontSize: 13,
                  outline: "none",
                  background: "white",
                }}
              />
              <button
                onClick={onSend}
                disabled={!draft.trim()}
                style={{
                  background: "#25D366",
                  color: "white",
                  border: "none",
                  width: 40,
                  height: 40,
                  borderRadius: 20,
                  cursor: draft.trim() ? "pointer" : "not-allowed",
                  opacity: draft.trim() ? 1 : 0.5,
                  fontSize: 16,
                }}
              >
                ▶
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const btnGhost = {
  padding: "6px 14px",
  borderRadius: 6,
  background: "white",
  color: "#475569",
  border: "1px solid #E2E8F0",
  fontWeight: 600,
  fontSize: 12,
  cursor: "pointer",
};

export default WhatsAppInbox;
